import React, { useEffect, useState, useCallback } from "react";
import { X, Wallet, Pencil, Trash2, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatIDR, formatDate, todayISO, CATEGORIES, PAY_STATUS_LABEL } from "@/lib/format";
import { waInvoiceUrl } from "@/lib/exporters";
import { Button, Field, SeverityTag } from "@/components/kit";

const PTYPE_LABEL = { DP: "DP", Installment: "Cicilan", Settlement: "Pelunasan", Full: "Lunas", Payment: "Pembayaran" };

export default function TransactionDetail({ txn, canEdit, canDelete, onClose, onChanged }) {
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // 'pay' | 'edit'
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (txn.bill_id) {
        setBill(await api.getBill(txn.bill_id));
      } else {
        setBill({
          total: txn.amount, paid: txn.amount, remaining: 0, status: "PAID",
          payments: [{ id: txn.id, amount: txn.amount, payment_type: txn.payment_type || "Full", payment_method: txn.payment_method, date: (txn.transaction_at || txn.created_at || "").slice(0, 10), note: txn.note, recorded_by: txn.recorded_by }],
        });
      }
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [txn]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => { await load(); onChanged?.(); };

  const sendWa = async () => {
    const win = window.open("", "_blank", "noopener");
    try {
      const m = await api.getMember(txn.member_id);
      const url = waInvoiceUrl(m, { ...txn, created_at: txn.transaction_at || txn.created_at }, bill);
      if (win) win.location.href = url; else window.open(url, "_blank", "noopener");
    } catch (e) { if (win) win.close(); toast.error(e.message); }
  };

  const del = async () => {
    if (!window.confirm("Hapus transaksi ini? Ledger, spending & poin akan dihitung ulang.")) return;
    setBusy(true);
    try { await api.deleteTransaction(txn.id); toast.success("Transaksi dihapus"); onChanged?.(); onClose(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative flex h-full w-full max-w-md flex-col border-l border-[#2C2C2E] bg-[#0A0A0C] shadow-2xl fade-in" data-testid="transaction-detail-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[#1F1F22] px-5 py-4">
          <div className="hsc-label">Detail Transaksi</div>
          <button onClick={onClose} className="rounded-full p-1.5 text-[#8E8E93] hover:bg-[#141416] hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        {loading ? (
          <div className="flex flex-1 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#6E6E73]" /></div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-5">
            <div className="mb-5">
              <div className="font-display text-2xl text-white">{txn.member_name}</div>
              <div className="mt-1 flex items-center gap-2 text-sm text-[#8E8E93]">
                <span>{txn.category}</span>
                {txn.txn_type === "membership" && <span className="rounded bg-[#141416] px-1.5 py-0.5 text-[10px] text-[#8FD3E8]">MEMBERSHIP</span>}
                {txn.class_name && <span className="text-[#6E6E73]">· {txn.class_name}</span>}
                {txn.court && <span className="text-[#6E6E73]">· {txn.court}</span>}
              </div>
              <div className="mt-1 text-[12px] text-[#6E6E73]">Tanggal transaksi: {formatDate(txn.transaction_at || txn.created_at)}</div>
            </div>

            <div className="mb-5 rounded-2xl border border-[#1F1F22] bg-[#0E0E10] p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="hsc-label">Ringkasan Keuangan</span>
                <SeverityTag value={bill.status} />
              </div>
              <div className="space-y-2 text-sm">
                <Row label="Total Tagihan" value={formatIDR(bill.total)} />
                <Row label="Total Dibayar" value={formatIDR(bill.paid)} accent="#7CD992" />
                <Row label="Sisa Pembayaran" value={formatIDR(bill.remaining)} accent={bill.remaining > 0 ? "#FF8080" : "#6E6E73"} bold />
                <div className="pt-1 text-[12px] text-[#6E6E73]">Status: {PAY_STATUS_LABEL[bill.status] || bill.status}</div>
              </div>
            </div>

            {Array.isArray(txn.addons) && txn.addons.length > 0 && (
              <div className="mb-5" data-testid="line-items">
                <div className="hsc-label mb-2">Rincian Item</div>
                <div className="space-y-1.5 rounded-2xl border border-[#1F1F22] bg-[#0E0E10] p-4 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-white">{txn.category}{txn.court ? ` · ${txn.court}` : ""}</span>
                    <span className="text-white">{formatIDR((txn.amount || 0) - txn.addons.reduce((s, a) => s + (Number(a.amount) || 0), 0))}</span>
                  </div>
                  {txn.addons.map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-[#A1A1A6]">
                      <span>+ {a.label}</span><span>{formatIDR(Number(a.amount) || 0)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between border-t border-[#1F1F22] pt-1.5 font-semibold text-white">
                    <span>Subtotal</span><span>{formatIDR(txn.amount || 0)}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-5">
              <div className="hsc-label mb-2">Riwayat Pembayaran</div>
              <div className="space-y-2">
                {bill.payments.map((p, i) => (
                  <div key={p.id || i} className="rounded-xl border border-[#1F1F22] px-3 py-2.5" data-testid="payment-history-row">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white">{PTYPE_LABEL[p.payment_type] || p.payment_type || "Pembayaran"}</span>
                      <span className="text-sm font-medium text-white">{formatIDR(p.amount)}</span>
                    </div>
                    <div className="mt-0.5 text-[11px] text-[#6E6E73]">
                      {formatDate(p.date)}{p.payment_method ? ` · ${p.payment_method}` : ""}{p.recorded_by ? ` · ${p.recorded_by}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {mode === "pay" && <PayForm remaining={bill.remaining} onCancel={() => setMode(null)} onSave={async (body) => {
              setBusy(true);
              try { await api.addPayment(txn.id, body); toast.success("Pembayaran dicatat"); setMode(null); await refresh(); }
              catch (e) { toast.error(e.message); } finally { setBusy(false); }
            }} busy={busy} />}

            {mode === "edit" && <EditForm txn={txn} onCancel={() => setMode(null)} onSave={async (body) => {
              setBusy(true);
              try { await api.updateTransaction(txn.id, body); toast.success("Tersimpan"); setMode(null); await refresh(); }
              catch (e) { toast.error(e.message); } finally { setBusy(false); }
            }} busy={busy} />}
          </div>
        )}

        {!loading && !mode && (
          <div className="border-t border-[#1F1F22] px-5 py-4">
            <div className="grid grid-cols-2 gap-2">
              {canEdit && bill.remaining > 0 && (
                <Button className="col-span-2" onClick={() => setMode("pay")} data-testid="detail-pelunasan"><Wallet className="h-4 w-4" /> Pelunasan / Tambah Bayar</Button>
              )}
              <Button variant="outline" onClick={sendWa} data-testid="detail-whatsapp"><MessageCircle className="h-4 w-4" /> Invoice WA</Button>
              {canEdit && <Button variant="outline" onClick={() => setMode("edit")} data-testid="detail-edit"><Pencil className="h-4 w-4" /> Edit</Button>}
              {canDelete && <Button variant="danger" className="col-span-2" onClick={del} disabled={busy} data-testid="detail-delete"><Trash2 className="h-4 w-4" /> Hapus Transaksi</Button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value, accent, bold }) => (
  <div className="flex items-center justify-between">
    <span className="text-[#8E8E93]">{label}</span>
    <span className={bold ? "font-semibold" : ""} style={accent ? { color: accent } : { color: "#fff" }}>{value}</span>
  </div>
);

function PayForm({ remaining, onCancel, onSave, busy }) {
  const [amount, setAmount] = useState(remaining || "");
  const [method, setMethod] = useState("Transfer");
  const [date, setDate] = useState(todayISO());
  return (
    <div className="rounded-2xl border border-[#2C2C2E] bg-[#0E0E10] p-4">
      <div className="mb-3 hsc-label">Tambah Pembayaran</div>
      <div className="space-y-3">
        <Field label={`Jumlah (sisa ${formatIDR(remaining)})`}><input type="number" className="hsc-input" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pay-amount" /></Field>
        <Field label="Metode"><select className="hsc-input" value={method} onChange={(e) => setMethod(e.target.value)}>{["Transfer", "Cash", "QRIS", "Debit/Credit"].map((m) => <option key={m}>{m}</option>)}</select></Field>
        <Field label="Tanggal Pembayaran"><input type="date" className="hsc-input" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} /></Field>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>Batal</Button>
          <Button disabled={busy} onClick={() => { if (!Number(amount)) return toast.error("Isi jumlah"); onSave({ amount: Number(amount), payment_method: method, txn_date: date }); }} data-testid="pay-save">Catat</Button>
        </div>
      </div>
    </div>
  );
}

function EditForm({ txn, onCancel, onSave, busy }) {
  const initAddons = Array.isArray(txn.addons) ? txn.addons.map((a) => ({ label: a.label, amount: String(a.amount) })) : [];
  const initAddonSum = initAddons.reduce((s, a) => s + (parseInt(a.amount, 10) || 0), 0);
  const [addons, setAddons] = useState(initAddons);
  const [main, setMain] = useState(String((txn.amount || 0) - initAddonSum));
  const [category, setCategory] = useState(txn.category);
  const [txnType, setTxnType] = useState(txn.txn_type || "visit");
  const [note, setNote] = useState(txn.note || "");
  const [date, setDate] = useState((txn.transaction_at || txn.created_at || "").slice(0, 10) || todayISO());
  const toInt = (v) => parseInt(String(v).replace(/[^0-9]/g, ""), 10) || 0;
  const addonSum = addons.reduce((s, a) => s + toInt(a.amount), 0);
  const total = toInt(main) + addonSum;
  const hasAddons = addons.length > 0 || initAddons.length > 0;
  return (
    <div className="rounded-2xl border border-[#2C2C2E] bg-[#0E0E10] p-4">
      <div className="mb-3 hsc-label">Edit Transaksi</div>
      <div className="space-y-3">
        <Field label={hasAddons ? "Harga Utama (Rp)" : "Jumlah (Rp)"}><input type="number" className="hsc-input" value={main} onChange={(e) => setMain(e.target.value)} data-testid="edit-amount" /></Field>

        <div className="space-y-2 rounded-xl border border-[#1F1F22] p-3">
          <div className="flex items-center justify-between">
            <span className="hsc-label">Add-on</span>
            <button type="button" className="text-xs text-[#8FD3E8] hover:text-white" onClick={() => setAddons((p) => [...p, { label: "Lainnya", amount: "" }])} data-testid="edit-addon-add">+ Tambah</button>
          </div>
          {addons.map((a, idx) => (
            <div key={idx} className="flex items-center gap-2" data-testid={`edit-addon-row-${idx}`}>
              <input className="hsc-input w-28 shrink-0" value={a.label} onChange={(e) => setAddons((p) => p.map((x, i) => (i === idx ? { ...x, label: e.target.value } : x)))} />
              <input type="number" className="hsc-input flex-1" placeholder="Nominal" value={a.amount} onChange={(e) => setAddons((p) => p.map((x, i) => (i === idx ? { ...x, amount: e.target.value } : x)))} data-testid={`edit-addon-amount-${idx}`} />
              <button type="button" className="rounded-lg p-1.5 text-[#FF8080] hover:bg-[#160b0b]" onClick={() => setAddons((p) => p.filter((_, i) => i !== idx))} data-testid={`edit-addon-remove-${idx}`}>✕</button>
            </div>
          ))}
          {addons.length === 0 && <div className="text-[12px] text-[#6E6E73]">Tidak ada add-on.</div>}
        </div>

        <div className="flex items-center justify-between rounded-lg bg-[#141416] px-3 py-2 text-sm">
          <span className="text-[#8E8E93]">Total (utama + add-on)</span>
          <span className="font-semibold text-white" data-testid="edit-total">{formatIDR(total)}</span>
        </div>

        <Field label="Kategori"><select className="hsc-input" value={category} onChange={(e) => setCategory(e.target.value)}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
        <Field label="Tipe"><select className="hsc-input" value={txnType} onChange={(e) => setTxnType(e.target.value)}><option value="visit">Visit</option><option value="membership">Membership</option></select></Field>
        <Field label="Tanggal Transaksi"><input type="date" className="hsc-input" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="Catatan"><input className="hsc-input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>Batal</Button>
          <Button disabled={busy} onClick={() => {
            if (total <= 0) return toast.error("Total harus lebih dari 0");
            const cleanAddons = addons.map((a) => ({ label: a.label || "Lainnya", amount: toInt(a.amount) })).filter((a) => a.amount > 0);
            onSave({ amount: total, category, txn_type: txnType, note, txn_date: date, addons: cleanAddons });
          }} data-testid="edit-save">Simpan</Button>
        </div>
      </div>
    </div>
  );
}
